import React, { useEffect } from "react";
import styles from "./RolesPermissonForm.module.scss";
import InputField from "../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import { Col, Collapse, message } from "antd";
import {
  activeKeys,
  buildPanelDataFromPermissions,
  ManageRolesConsts,
  SectionList,
} from "./constants";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonSize,
  ButtonType,
  CommonButtonTexts,
} from "../../../enums/ButtonAttributesEnums";
import { useFormContext } from "react-hook-form";
import RolesServices from "../../../services/roles.services";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { appRoutes } from "../../../constants/appRoutes";
import TNLoader from "../../../reusable-components/TNLoader";
import { storageKeys } from "../../../constants/storageKeys";

export interface RolesPermissionsFormParams {
  PanelData?: [];
  isEdit?: boolean;
  roleId?: string;
}

const RolesPermissionsForm = (params: RolesPermissionsFormParams) => {
  const navigate = useHistory();
  const { Panel } = Collapse;
  const { PanelData, isEdit, roleId } = params;
  const {
    arrowPosition,
    borderedValue,
    ROLE_NAME_LABEL,
    ROLE_NAME_NAME,
    ROLE_NAME_PLACEHOLDER,
    DASHBOARD_LABEL,
    DASHBOARD_NAME,
    NONE_SELECTED_WARNING,
  } = ManageRolesConsts;
  const { watch, getValues, handleSubmit, reset } = useFormContext();
  const { AddUserRoleDetails, UpdateUserRoleDetails } = RolesServices();
  const { mutateAsync: AddRole, isPending: submitPending } =
    AddUserRoleDetails();
  const { mutateAsync: UpdateRole, isPending: updatePending } =
    UpdateUserRoleDetails(roleId);

  const handleNavigate = () => {
    navigate.goBack();
  };

  const handleCancel = () => {
    reset();
    handleNavigate();
  };

  const handleSave = async () => {
    const values = getValues();
    if (!Object.values(values).includes(true)) {
      message.warning(NONE_SELECTED_WARNING);
      return;
    }
    const otherData = buildPanelDataFromPermissions(values, PanelData);
    const payload = {
      otherData: otherData,
      roleName: getValues(ROLE_NAME_NAME),
    };
    if (isEdit) {
      await UpdateRole(payload, {
        onSuccess: () => {
          handleNavigate();
          localStorage.removeItem(storageKeys.ROLE_DATA_KEY);
        },
      });
    } else {
      await AddRole(payload, {
        onSuccess: () => {
          handleNavigate();
        },
      });
    }
  };

  return (
    <>
      <TNLoader isLoading={submitPending || updatePending} />
      <div className={styles.rolesPermissionsWrapper}>
        <div className={styles.roleTitleField}>
          <InputField
            name={ROLE_NAME_NAME}
            label={ROLE_NAME_LABEL}
            placeholder={ROLE_NAME_PLACEHOLDER}
            type={InputfieldTypes.TEXT}
          />
        </div>
        <div className={styles.permissionsList}>
          <div className={styles.dashBoardOption}>
            <span className={styles.dashBoardOptionTitle}>
              {DASHBOARD_LABEL}
            </span>
            <div className={styles.dashBoardOptionToggle}>
              <InputField
                name={DASHBOARD_NAME}
                type={InputfieldTypes.SWITCH}
                defaultSwitch={getValues(DASHBOARD_NAME)}
                value={watch(DASHBOARD_NAME)}
              />
            </div>
          </div>
          <Collapse
            ghost
            expandIconPosition={arrowPosition}
            bordered={borderedValue}
            defaultActiveKey={activeKeys}
          >
            {SectionList.map((item) => (
              <Panel
                key={item.key}
                header={item.label}
                className={styles.sectionHeader}
              >
                {item.children}
              </Panel>
            ))}
          </Collapse>
        </div>
        <div className={styles.buttonsContainer}>
          <TNButton
            shape={ButtonType.DEFAULT}
            size={ButtonSize.MEDIUM}
            text={CommonButtonTexts.CANCEL}
            className={styles.cancelButton}
            onClick={() => handleCancel()}
            disabled={submitPending || updatePending}
          />
          <TNButton
            shape={ButtonType.DEFAULT}
            size={ButtonSize.MEDIUM}
            text={CommonButtonTexts.SAVE}
            onClick={handleSubmit(() => handleSave())}
            disabled={submitPending || updatePending}
          />
        </div>
      </div>
    </>
  );
};

export default RolesPermissionsForm;
